import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import googleDriveAxios from "../../common/googleDriveAxios";

const RoofAndJMouldChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [wallAssemblyChecklistItems, setWallAssemblyChecklistItems] = useState<
    Checklist[]
  >([]);
  const [jMouldChecklistItems, setJMouldChecklistItems] = useState<Checklist[]>(
    []
  );

  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const hasUnsavedChanges = chassisNo;

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);
  const checkCheckpointRecord = useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "roof-inspection",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [chassisNo, checkCheckpointRecord]);

  // Warn user before leaving if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const fetchData = useCallback(async () => {
    if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
      setWallAssemblyChecklistItems([]);
      setJMouldChecklistItems([]);
      setApiError(false);
      setModel("");
      setCheckedBy("");
      setCompleteStatus("Incomplete");
      return;
    }

    setLoading(true);
    setApiError(false);
    setModel("");
    try {
      const response = await axiosPrivate.post(
        `/prepare_roof_inspection/${chassisNo}`
      );
      const data = response.data;

      // Normalize status values to lowercase
      const externalData = (data.wallAssemblyChecklistItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const internalData = (data.jMouldChecklistItems || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const applianceData = (data.appliance || []).map((item: Checklist) => ({
        ...item,
        status: item.status ? item.status.trim().toLowerCase() : "not checked",
      }));
      const cupboardData = (data.electricalOverheadCupboard || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const conduitsData = (data.conduits || []).map((item: Checklist) => ({
        ...item,
        status: item.status ? item.status.trim().toLowerCase() : "not checked",
      }));
      const otherData = (data.other || []).map((item: Checklist) => ({
        ...item,
        status: item.status ? item.status.trim().toLowerCase() : "not checked",
      }));

      setWallAssemblyChecklistItems(externalData);
      setJMouldChecklistItems(internalData);

      setLocalCheckedBy(data.checkedBy || "");
      setCheckedBy(data.checkedBy || "");
      setModel(data.model || "");

      let formattedDate = "";
      if (data.createdOn) {
        const date = new Date(data.createdOn);
        if (date.getFullYear() !== 1900) {
          formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
        }
      }
      setDate(formattedDate);

      const allItems = [
        ...externalData,
        ...internalData,
        ...applianceData,
        ...cupboardData,
        ...conduitsData,
        ...otherData,
      ];
      const isComplete =
        allItems.length > 0 &&
        allItems.every(
          (item) =>
            item.status &&
            ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
        );
      console.log(
        "Fetched isComplete:",
        isComplete,
        "Setting completeStatus:",
        isComplete ? "Complete" : "Incomplete"
      );
      setCompleteStatus(isComplete ? "Complete" : "Incomplete");
    } catch (error: any) {
      if (error.response?.status === 500) {
        setApiError(true);
      } else {
        toast.error("Failed to fetch data.");
      }
    } finally {
      setLoading(false);
    }
  }, [chassisNo, setModel, axiosPrivate, setCheckedBy]);

  useEffect(() => {
    fetchData();
  }, [chassisNo, setModel, axiosPrivate, setCheckedBy, fetchData]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      const allChecklists = [
        ...wallAssemblyChecklistItems,
        ...jMouldChecklistItems,
      ];

      const isComplete =
        allChecklists.length > 0 &&
        allChecklists.every(
          (item) =>
            item.status &&
            ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
        );
      console.log(
        "Saving - isComplete:",
        isComplete,
        "Current completeStatus:",
        completeStatus
      );

      const payload = {
        chassisNo,
        model: model,
        wallAssemblyChecklistItems: wallAssemblyChecklistItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        jMouldChecklistItems: jMouldChecklistItems.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
        completeStatus: isComplete ? "Complete" : "Incomplete",
      };

      await toast.promise(
        axiosPrivate.post("/create_or_update_roof_inspection_prep", payload),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      setCompleteStatus(isComplete ? "Complete" : "Incomplete");
      await fetchData();

      if (checkedBy) {
        const allChecklists = [
          ...wallAssemblyChecklistItems,
          ...jMouldChecklistItems,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr = item.notes || "";

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part) => part.trim())
                .filter((part) => part);
              return parts.map((note) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${notesStr}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "roof-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "roof-inspection" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;

    if (type === "wall_assembly") {
      const updatedData = [...wallAssemblyChecklistItems];
      updatedData[index].notes = value;
      setWallAssemblyChecklistItems(updatedData);
    }
  };

  const handleRadioChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value.toLowerCase();
      setChecklist(updatedData);
    };

    if (type === "wall_assembly") {
      updateChecklist(
        wallAssemblyChecklistItems,
        setWallAssemblyChecklistItems
      );
    }
  };

  const allChecklists = [
    ...wallAssemblyChecklistItems,
    ...jMouldChecklistItems,
  ];

  const allNotChecked =
    allChecklists.length > 0 &&
    allChecklists.every((item) => item.status?.toLowerCase() === "not checked");

  const checklistsWithSerialNumbers = [
    {
      data: wallAssemblyChecklistItems,
      type: "wall_assembly",
      subHeaderText: "Wall Assembly",
    },
  ];

  let serialNumberCounter = 0;

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    sx={{
                      position: "absolute",
                      top: "60%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : apiError ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="error">Invalid DC Number</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <React.Fragment>
                {checklistsWithSerialNumbers.map((checklistSection) => {
                  const serialNumberStart = serialNumberCounter + 1;
                  serialNumberCounter += checklistSection.data.length;

                  console.log(
                    `Rendering ${checklistSection.type} with serialNumberStart: ${serialNumberStart}, item count: ${checklistSection.data.length}`
                  );

                  return (
                    <MultiSubheaderTableRowComponent
                      key={checklistSection.type}
                      subHeaderText={checklistSection.subHeaderText}
                      type={checklistSection.type}
                      data={checklistSection.data}
                      handleNotesChange={handleNotesChange}
                      handleRadioChange={handleRadioChange}
                      disabled={completeStatus === "Complete"}
                      allNotChecked={allNotChecked}
                      serialNumberStart={serialNumberStart}
                      chassisDCNumber={"DC" + chassisNo}
                      deptName={"roof-inspection"}
                      activeCheckpointList={activeCheckpointList}
                      checkCheckpointRecord={checkCheckpointRecord}
                    />
                  );
                })}
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>
      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default RoofAndJMouldChecklist;
